/**
 * @param {Record<string, unknown>} capture
 * @returns {{ lat: number, lng: number } | null}
 */
export function getCaptureLatLng(capture) {
  if (!capture || typeof capture !== 'object') return null;
  const aec = capture.aecContext && typeof capture.aecContext === 'object' ? capture.aecContext : null;
  const gps = aec?.gps && typeof aec.gps === 'object' ? aec.gps : null;
  const rawLat = capture.lat ?? gps?.lat;
  const rawLng = capture.lng ?? gps?.lng;
  const lat = typeof rawLat === 'number' ? rawLat : parseFloat(String(rawLat ?? ''));
  const lng = typeof rawLng === 'number' ? rawLng : parseFloat(String(rawLng ?? ''));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * Lat/lng for marketing footer / labels: mapPlacement anchor, then top-level or AEC GPS, then GCP centroid.
 * @param {Record<string, unknown> | null | undefined} capture
 * @returns {{ lat: number, lng: number } | null}
 */
export function getCaptureFooterLatLng(capture) {
  if (!capture || typeof capture !== 'object') return null;
  const mp = getCaptureMapPlacement(capture);
  if (mp && Number.isFinite(mp.lat) && Number.isFinite(mp.lng)) {
    return { lat: mp.lat, lng: mp.lng };
  }
  const direct = getCaptureLatLng(capture);
  if (direct) return direct;
  const rawMp = capture.mapPlacement;
  if (rawMp && typeof rawMp === 'object' && Array.isArray(rawMp.gcps) && rawMp.gcps.length > 0) {
    let sumLat = 0;
    let sumLng = 0;
    let n = 0;
    for (const g of rawMp.gcps) {
      if (!g || typeof g !== 'object') continue;
      const plat = typeof g.lat === 'number' && Number.isFinite(g.lat) ? g.lat : parseFloat(String(g.lat ?? ''));
      const plng = typeof g.lng === 'number' && Number.isFinite(g.lng) ? g.lng : parseFloat(String(g.lng ?? ''));
      if (Number.isFinite(plat) && Number.isFinite(plng)) {
        sumLat += plat;
        sumLng += plng;
        n += 1;
      }
    }
    if (n > 0) return { lat: sumLat / n, lng: sumLng / n };
  }
  return null;
}

/**
 * Optional raw inputs from the 3D workspace Map Plot workflow (ground plane + north + GCP snapshot).
 * @param {Record<string, unknown>} mp
 */
function gcpCentroidLatLng(mp) {
  if (!mp || typeof mp !== 'object' || !Array.isArray(mp.gcps) || mp.gcps.length === 0) return null;
  let sumLat = 0;
  let sumLng = 0;
  let n = 0;
  for (const g of mp.gcps) {
    if (!g || typeof g !== 'object') continue;
    const plat = typeof g.lat === 'number' && Number.isFinite(g.lat) ? g.lat : parseFloat(String(g.lat ?? ''));
    const plng = typeof g.lng === 'number' && Number.isFinite(g.lng) ? g.lng : parseFloat(String(g.lng ?? ''));
    if (Number.isFinite(plat) && Number.isFinite(plng)) {
      sumLat += plat;
      sumLng += plng;
      n += 1;
    }
  }
  if (n === 0) return null;
  return { lat: sumLat / n, lng: sumLng / n };
}

function parseTriple(raw) {
  if (
    Array.isArray(raw)
    && raw.length === 3
    && raw.every((x) => typeof x === 'number' && Number.isFinite(x))
  ) {
    return raw;
  }
  return undefined;
}

function parseMapPlacementAlignmentFields(mp) {
  const alignmentSource =
    mp.alignmentSource === 'workflow' || mp.alignmentSource === 'manual' ? mp.alignmentSource : undefined;
  const northBearingSplatDeg =
    typeof mp.northBearingSplatDeg === 'number' && Number.isFinite(mp.northBearingSplatDeg)
      ? mp.northBearingSplatDeg
      : undefined;
  const residualMeters =
    typeof mp.residualMeters === 'number' && Number.isFinite(mp.residualMeters)
      ? mp.residualMeters
      : undefined;
  let groundPlane = null;
  const gp = mp.groundPlane;
  if (gp && typeof gp === 'object') {
    const norm = gp.normal;
    const pt = gp.pointOnPlane;
    const n =
      Array.isArray(norm) && norm.length === 3
        ? norm.map((v) => Number(v) || 0)
        : null;
    const p =
      Array.isArray(pt) && pt.length === 3
        ? pt.map((v) => Number(v) || 0)
        : null;
    if (n && p && (n[0] !== 0 || n[1] !== 0 || n[2] !== 0)) {
      groundPlane = { normal: n, pointOnPlane: p };
    }
  }
  const gcpsRaw = mp.gcps;
  let gcps = [];
  if (Array.isArray(gcpsRaw)) {
    gcps = gcpsRaw
      .map((g, idx) => {
        if (!g || typeof g !== 'object') return null;
        const rawId = typeof g.id === 'string' ? g.id.trim() : '';
        const id = rawId || `gcp-${idx}`;
        const sp = g.splat;
        const splat =
          Array.isArray(sp) && sp.length === 3
            ? [Number(sp[0]) || 0, Number(sp[1]) || 0, Number(sp[2]) || 0]
            : null;
        const plat = typeof g.lat === 'number' && Number.isFinite(g.lat) ? g.lat : parseFloat(String(g.lat ?? ''));
        const plng = typeof g.lng === 'number' && Number.isFinite(g.lng) ? g.lng : parseFloat(String(g.lng ?? ''));
        const ele =
          typeof g.ele === 'number' && Number.isFinite(g.ele) ? g.ele : undefined;
        if (!splat || !Number.isFinite(plat) || !Number.isFinite(plng)) return null;
        return { id, splat, lat: plat, lng: plng, ...(ele !== undefined ? { ele } : {}) };
      })
      .filter(Boolean);
  }
  const basisNorth = parseTriple(mp.basisNorth);
  const basisUp = parseTriple(mp.basisUp);
  return {
    alignmentSource,
    groundPlane,
    northBearingSplatDeg,
    residualMeters,
    gcps,
    ...(basisNorth ? { basisNorth } : {}),
    ...(basisUp ? { basisUp } : {}),
  };
}

/**
 * True when the capture was aligned in the 3D workspace Map Plot workflow (strict gate for first plot).
 * @param {Record<string, unknown>} capture
 */
export function isCaptureMapPlotWorkflowComplete(capture) {
  const mp = capture?.mapPlacement;
  if (!mp || typeof mp !== 'object') return false;
  if (mp.alignmentSource !== 'workflow') return false;
  const ap = parseMapPlacementAlignmentFields(mp);
  if (!ap.groundPlane || ap.northBearingSplatDeg === undefined) return false;
  if (!Array.isArray(ap.gcps) || ap.gcps.length < 3) return false;
  const rawLat = mp.lat ?? mp.latitude;
  const rawLng = mp.lng ?? mp.longitude;
  const lat = typeof rawLat === 'number' && Number.isFinite(rawLat) ? rawLat : parseFloat(String(rawLat ?? ''));
  const lng = typeof rawLng === 'number' && Number.isFinite(rawLng) ? rawLng : parseFloat(String(rawLng ?? ''));
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/** @param {Record<string, unknown>} capture */
export function captureNeedsMapPlotWorkflowBeforeGlobePlot(capture) {
  return !getCaptureMapPlacement(capture);
}

/**
 * Map tab placement (terrain-grounded pose). See Firestore_Schema `mapPlacement`.
 * `headingDeg` / `pitchDeg` / `rollDeg` / `scale` / `heightAboveTerrain` / `opacity` — matrix vs tile style (see MAP tab).
 * @param {Record<string, unknown>} capture
 * @returns {{
 *   lat: number,
 *   lng: number,
 *   sampledTerrainHeightMeters: number,
 *   heightAboveTerrain: number,
 *   headingDeg: number,
 *   pitchDeg: number,
 *   rollDeg: number,
 *   scale: number,
 *   opacity: number,
 *   plotted: boolean,
 *   alignmentSource?: 'workflow' | 'manual',
 *   groundPlane?: { normal: number[], pointOnPlane: number[] } | null,
 *   northBearingSplatDeg?: number,
 *   residualMeters?: number,
 *   gcps?: Array<{ id: string, splat: number[], lat: number, lng: number, ele?: number }>,
 * } | null}
 */
export function getCaptureMapPlacement(capture) {
  if (!capture || typeof capture !== 'object') return null;
  const mp = capture.mapPlacement;
  if (!mp || typeof mp !== 'object') return null;
  /** Support `lat`/`lng`, GeoPoint-style `latitude`/`longitude`, and numeric strings from older writes. */
  const rawLat = mp.lat ?? mp.latitude;
  const rawLng = mp.lng ?? mp.longitude;
  let lat = typeof rawLat === 'number' && Number.isFinite(rawLat) ? rawLat : parseFloat(String(rawLat ?? ''));
  let lng = typeof rawLng === 'number' && Number.isFinite(rawLng) ? rawLng : parseFloat(String(rawLng ?? ''));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    const centroid = gcpCentroidLatLng(mp);
    if (centroid) {
      lat = centroid.lat;
      lng = centroid.lng;
    }
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const sampledTerrainHeightMeters =
    typeof mp.sampledTerrainHeightMeters === 'number' && Number.isFinite(mp.sampledTerrainHeightMeters)
      ? mp.sampledTerrainHeightMeters
      : 0;
  const heightAboveTerrain =
    typeof mp.heightAboveTerrain === 'number' && Number.isFinite(mp.heightAboveTerrain)
      ? mp.heightAboveTerrain
      : 0;
  const headingDeg =
    typeof mp.headingDeg === 'number' && Number.isFinite(mp.headingDeg) ? mp.headingDeg : 0;
  const pitchDeg = typeof mp.pitchDeg === 'number' && Number.isFinite(mp.pitchDeg) ? mp.pitchDeg : 0;
  const rollDeg = typeof mp.rollDeg === 'number' && Number.isFinite(mp.rollDeg) ? mp.rollDeg : 0;
  const scale = typeof mp.scale === 'number' && Number.isFinite(mp.scale) && mp.scale > 0 ? mp.scale : 1;
  const rawOpacity = Number(mp.opacity);
  const opacity =
    Number.isFinite(rawOpacity) && rawOpacity >= 0 && rawOpacity <= 1 ? rawOpacity : 1;
  const plotted = mp.plotted === true;
  const ap = parseMapPlacementAlignmentFields(mp);
  return {
    lat,
    lng,
    sampledTerrainHeightMeters,
    heightAboveTerrain,
    headingDeg,
    pitchDeg,
    rollDeg,
    scale,
    opacity,
    plotted,
    ...(ap.alignmentSource ? { alignmentSource: ap.alignmentSource } : {}),
    ...(ap.groundPlane ? { groundPlane: ap.groundPlane } : {}),
    ...(ap.northBearingSplatDeg !== undefined ? { northBearingSplatDeg: ap.northBearingSplatDeg } : {}),
    ...(ap.residualMeters !== undefined ? { residualMeters: ap.residualMeters } : {}),
    ...(ap.gcps.length ? { gcps: ap.gcps } : {}),
    ...(ap.basisNorth ? { basisNorth: ap.basisNorth } : {}),
    ...(ap.basisUp ? { basisUp: ap.basisUp } : {}),
  };
}

/**
 * True when the workspace compass should show (full Map Plot workflow + solver RMS).
 * @param {Record<string, unknown>} capture
 */
export function captureHasWorkspaceCompassAlignment(capture) {
  if (!isCaptureMapPlotWorkflowComplete(capture)) return false;
  const mp = getCaptureMapPlacement(capture);
  if (!mp || mp.alignmentSource !== 'workflow') return false;
  return typeof mp.residualMeters === 'number' && Number.isFinite(mp.residualMeters);
}

/** @param {Record<string, unknown>} capture */
export function isCaptureMapPlotted(capture) {
  const mp = getCaptureMapPlacement(capture);
  return !!(mp && mp.plotted === true);
}

/**
 * @param {import('firebase/firestore').Timestamp | Date | undefined} ts
 */
export function captureTimestampMs(ts) {
  if (!ts) return 0;
  try {
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (ts instanceof Date) return ts.getTime();
  } catch {
    /* ignore */
  }
  return 0;
}

/**
 * @param {Array<Record<string, unknown>>} steps — included timeline snapshots
 * @param {string} captureId
 */
export function findTimelineIndexForCapture(steps, captureId) {
  if (!captureId || !Array.isArray(steps)) return -1;
  return steps.findIndex((s) => (s.captureId || s.id) === captureId);
}
